import React, { useMemo } from 'react'
import { t, Trans } from 'src/translations'
import { getLocale } from '@toasttab/buffet-pui-locale-utilities'

import {
  formatCurrency,
  Currency as NumberUtilsCurrency
} from '@toasttab/buffet-pui-number-utilities'
import { differenceInDays } from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'
import qs from 'query-string'

import { Currency, Money, useGuestWalletQuery } from 'src/apollo/onlineOrdering'
import { useFormatMoney } from 'src/lib/js/hooks/useFormatPrice'
import { useStaticSiteContext } from 'public/components/app/StaticSiteContext'
import {
  isToastLocalRequest,
  isToastOrderRequest
} from 'public/js/siteUtilities'
import Image from 'shared/components/common/Image'
import { useOptionalRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'

import { resources } from 'config/index'
import { ChevronRightIcon } from '@toasttab/buffet-pui-icons'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

const ToastCash = (props: { nowDate?: Date }) => {
  const {
    ooToastCashSpend: ooToastCashSpendEnabled,
    stpToastCashWalletWebEnabled
  } = useFlags()
  const optionalRestaurantGuid = useOptionalRestaurant()?.ooRestaurant?.guid
  const hasCustomer = Boolean(useCustomer().customer)

  const showToastCashBalance = optionalRestaurantGuid
    ? ooToastCashSpendEnabled
    : true

  const { data } = useGuestWalletQuery({
    skip: !hasCustomer || !showToastCashBalance,
    variables: {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // variable intentionally nullable; presence of stored value balance
      // determined by restaurant guid or its absence.
      restaurantId: optionalRestaurantGuid
    },
    // ensures fresh values if component re-mounts
    fetchPolicy: 'cache-and-network',
    ssr: false
  })
  const formatMoney = useFormatMoney()

  const amountExpiringMessage = React.useMemo(() => {
    const expiringValue =
      data?.guest.wallet.guestCurrencyAccount.nearestFutureExpirableDayValue
    if (!expiringValue) return null
    return transformExpirationToGuestMessage(
      expiringValue.amount,
      new Date(expiringValue.expirationDate),
      props.nowDate ?? new Date()
    )
  }, [
    data?.guest.wallet.guestCurrencyAccount.nearestFutureExpirableDayValue,
    props.nowDate
  ])

  if (!data || !showToastCashBalance) {
    return null
  }

  const availableBalance = optionalRestaurantGuid
    ? data.guest.wallet.guestCurrencyAccount.availableBalanceByRestaurant
    : data.guest.wallet.guestCurrencyAccount.availableBalanceWithStoredValue

  return (
    <div className='toastCashCardContainer' aria-labelledby='#toast-cash-funds'>
      <ToastCashCardLinkOrBlock
        isLink={stpToastCashWalletWebEnabled}
        restaurantGuid={optionalRestaurantGuid}
      >
        <div className='toastCashContentsContainer'>
          <div className='toastCashCardContents' id='toast-cash-funds'>
            <div className='toastCashCardHeader'>
              {t('toast-cash.toast-cash')}
            </div>
            <div className='toastCashDetailsContainer'>
              <div className='toastCashDetails'>
                <div className='toastCashAmount'>
                  {formatMoney(availableBalance as Money)}
                </div>
                <div className='toastCashAvailableFunds'>
                  {amountExpiringMessage
                    ? amountExpiringMessage
                    : t('toast-cash.available-funds')}
                </div>
              </div>
              {stpToastCashWalletWebEnabled && (
                <ChevronRightIcon
                  aria-label={t('toast-cash.visit-toast-wallet')}
                />
              )}
            </div>
          </div>
          <Image
            alt='Toast Cash'
            src='illustrations/toast-cash-card-decoration.svg'
          />
        </div>
      </ToastCashCardLinkOrBlock>
      <div className='toastCashDisclaimer'>
        <Trans
          i18nKey='toast-cash.toast-cash-disclaimer'
          components={{
            Link: (
              <a
                target='_blank'
                rel='noopener noreferrer'
                href='https://pos.toasttab.com/toast-cash-agreement'
              />
            )
          }}
        />
      </div>
    </div>
  )
}

const ToastCashCardLinkOrBlock = (props: {
  isLink: boolean
  restaurantGuid?: string
  children: React.ReactNode
}) => {
  const staticContext = useStaticSiteContext()
  const { accountPath } = useRestaurantRoutes()
  const isOnAccountPage = useMemo(
    () =>
      typeof window !== 'undefined' && accountPath === window.location.pathname,
    [accountPath]
  )

  const isToastOrder = isToastOrderRequest(staticContext)
  const isToastLocal = isToastLocalRequest(staticContext)

  let entrypoint: undefined | 'toastOnlineOrdering' | 'toastLocal'
  if (isToastLocal) {
    entrypoint = 'toastLocal'
  } else if (isToastOrder || isOnAccountPage) {
    entrypoint = 'toastOnlineOrdering'
  }

  if (props.isLink && entrypoint) {
    const queryStringSearch = qs.stringify({
      entrypoint: entrypoint,
      restaurantGuid: props.restaurantGuid
    })
    return (
      <a
        href={`https://${resources.toastOrderHost}/wallet?${queryStringSearch}`}
        target='_blank'
        className='toastCashCard'
        rel='noreferrer'
      >
        {props.children}
      </a>
    )
  }
  return <div className='toastCashCard'>{props.children}</div>
}

/**
 *
 * @param moneyAmount
 * @param expirationDate
 * @returns A message about near expiring credits.
 * Returns null if no user-facing message should be supplied
 */
function transformExpirationToGuestMessage(
  moneyAmount: Money,
  expirationDate: Date,
  nowDate: Date
) {
  if (moneyAmount.currency !== Currency.Usd) {
    return null
  }

  if (Number(moneyAmount.amount) <= 0) {
    return null
  }

  const difference = differenceInDays(expirationDate, nowDate)

  if (difference < 0) {
    // If I get a time in the past, that is a smell that my client should be refreshing.
    // If client gets stale, indicated with a time in the past, it should trigger a refetch. I'm just going to avoid that complexity for now.
    // For now, we'll just show no message if that case does occur, and fall back to a default treatment.
    return null
  }

  const moneyFormatted = `${formatCurrency(
    { amount: moneyAmount.amount, currency: NumberUtilsCurrency.USD },
    getLocale(),
    { trailingZeros: false }
  )}`

  if (difference === 0) {
    return t('profile-wallet.days-formatted.expiring_today', {
      moneyFormatted
    })
  } else if (difference === 1) {
    return t('profile-wallet.days-formatted.expiring_one-day', {
      moneyFormatted
    })
  } else {
    return t('profile-wallet.days-formatted.expiring_many-days', {
      moneyFormatted,
      dayCount: difference
    })
  }
}

export default ToastCash
